import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertCircle, Heart, Phone, Mail, ArrowRight } from 'lucide-react';
import Seo from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import { BUSINESS } from '@/seo/seoConfig';

export default function Safeguarding() {
  return (
    <div>
      <Seo
        title="Safeguarding Policy | Ministry of Jesus, Dublin"
        titleExact
        description="Safeguarding policy of Ministry of Jesus (Registered Charity RCN 20207036) — our commitment to protecting children, young people and vulnerable adults in accordance with Irish safeguarding standards and Túsla guidance."
        path="/safeguarding"
        keywords={['Ministry of Jesus safeguarding', 'church child protection Ireland', 'RCN 20207036 safeguarding policy', 'Túsla']}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Safeguarding Policy', path: '/safeguarding' }])]}
      />

      <section className="gradient-purple text-white py-10 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Shield size={14}/> Child &amp; Vulnerable Adult Protection
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-3">Safeguarding Policy</h1>
          <p className="text-white/85">Our commitment to a safe church for every child and every vulnerable adult.</p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-moj-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
          <p className="text-moj-charcoal/85 leading-relaxed">
            Ministry of Jesus Ireland (Registered Charity <strong>RCN 20207036</strong>) is committed to providing a safe environment for all children,
            young people and vulnerable adults who take part in our worship services, Sunday School, Vacation Bible School and other church activities.
          </p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">Our commitment</h2>
          <p>We uphold the safeguarding standards set by the Charities Regulator, Túsla (the Child and Family Agency), and the National Vetting Bureau. We believe that every child and vulnerable person has the right to be treated with respect, kept safe from harm, listened to, and taken seriously.</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">Key principles</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>All volunteers and staff working with children or vulnerable adults are Garda vetted through the National Vetting Bureau before beginning ministry.</li>
            <li>Every volunteer completes annual safeguarding training that covers recognising, responding to and reporting abuse.</li>
            <li>Two adults are present at all times during children’s ministry activities.</li>
            <li>Registration and consent forms are collected for every child attending Sunday School or VBS, including emergency contacts, medical information and photography consent.</li>
            <li>Children are supervised at all times — including drop-off, activities and pick-up.</li>
            <li>Physical contact is limited, appropriate and only used in the context of care, first aid or child-initiated hugs with witnesses present.</li>
            <li>Any concern about a child’s welfare is reported to the Designated Liaison Person (DLP) immediately and, where appropriate, to Túsla and An Garda Síochána.</li>
          </ul>

          <div className="card-moj p-5 border-l-4 border-red-500 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle size={22} className="text-red-600 flex-shrink-0 mt-0.5"/>
              <div>
                <h3 className="font-serif text-lg text-moj-purple-deep mb-1">Raising a concern</h3>
                <p className="text-sm text-moj-charcoal">
                  If you have a safeguarding concern about any child or vulnerable adult in our church community, please contact our Designated Liaison Person immediately by phoning
                  <a className="text-moj-purple underline mx-1" href={`tel:${BUSINESS.phone.replace(/\s/g,'')}`}>{BUSINESS.phone}</a>
                  or emailing <a className="text-moj-purple underline" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>. In an emergency, contact An Garda Síochána on <strong>112</strong> or <strong>999</strong>. You can also contact Túsla directly via
                  <a className="text-moj-purple underline ml-1" href="https://www.tusla.ie" target="_blank" rel="noopener noreferrer">tusla.ie</a>.
                </p>
              </div>
            </div>
          </div>

          <h2 className="font-serif text-2xl text-moj-purple-deep">Vetting &amp; training</h2>
          <p>All persons in leadership or with regular access to children or vulnerable adults are required to hold current Garda vetting through the National Vetting Bureau. Our Sunday School, VBS and youth teams receive refresher safeguarding training each year.</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">Record keeping</h2>
          <p>Safeguarding records are kept confidentially for the periods required by Túsla guidance (typically 25 years from the child’s 18th birthday). Access is strictly limited to the DLP and any responsible statutory authority.</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">Review</h2>
          <p>This policy is reviewed annually by the Trustees of Ministry of Jesus Ireland and updated to reflect changes in Irish safeguarding law and best practice.</p>

          <div className="card-moj p-5 flex items-center gap-3 mt-8">
            <Heart size={22} className="text-moj-gold-deep"/>
            <div className="text-sm text-moj-charcoal">Registered Charity Number: <strong className="text-moj-purple-deep">RCN 20207036</strong></div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" className="btn-primary"><Mail size={16}/> Contact Us <ArrowRight size={16}/></Link>
            <Link to="/privacy" className="btn-outline"><Shield size={16}/> Privacy Notice</Link>
            <a href={`tel:${BUSINESS.phone.replace(/\s/g,'')}`} className="btn-outline"><Phone size={16}/> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
